// src/types/mobileCrewTypes.ts
// ─────────────────────────────────────────────────────────────────
// Mobile & Crew — Phase 1 Type Definitions
// These interfaces match the future database schema (see DB schema
// design document 1F-01) so no types need to change at beta.
// Service layer (src/services/) returns these types from mock data now
// and from API responses at beta.
// ─────────────────────────────────────────────────────────────────

// ── 1A-01  Notification Bus Types ─────────────────────────────────

export type NotificationType =
  | 'ACCOMMODATION'
  | 'INCIDENT_ALERT'
  | 'SWMS_UPDATE'
  | 'PERMIT_TO_WORK'
  | 'TOOLBOX_SIGNOFF'
  | 'PLANT_DEFECT'
  | 'MATERIAL_READY'
  | 'ITP_RELEASE'
  | 'TIMESHEET_APPROVED'
  | 'ENVIRONMENTAL_ALERT'
  | 'PROGRESS_MILESTONE'
  | 'CUSTOM_WHSO_ALERT'
  | 'CREDENTIAL_EXPIRY'
  | 'PAYSLIP_AVAILABLE'

export const NOTIFICATION_TYPE_META: Record<NotificationType, {
  label: string
  icon: string
  category: 'safety' | 'workforce' | 'quality' | 'operational' | 'payroll'
  defaultPriority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  requiresAck: boolean
}> = {
  ACCOMMODATION:        { label: 'Accommodation',           icon: '🏨', category: 'operational', defaultPriority: 'HIGH',     requiresAck: true  },
  INCIDENT_ALERT:       { label: 'Safety Incident',         icon: '⚠️', category: 'safety',      defaultPriority: 'CRITICAL', requiresAck: true  },
  SWMS_UPDATE:          { label: 'SWMS Re-sign Required',   icon: '📋', category: 'safety',      defaultPriority: 'CRITICAL', requiresAck: true  },
  PERMIT_TO_WORK:       { label: 'Permit to Work',          icon: '🔐', category: 'safety',      defaultPriority: 'HIGH',     requiresAck: true  },
  TOOLBOX_SIGNOFF:      { label: 'Toolbox Sign-off',        icon: '✅', category: 'safety',      defaultPriority: 'MEDIUM',   requiresAck: true  },
  PLANT_DEFECT:         { label: 'Plant Defect Alert',      icon: '🚧', category: 'safety',      defaultPriority: 'HIGH',     requiresAck: true  },
  MATERIAL_READY:       { label: 'Materials Ready',         icon: '📦', category: 'operational', defaultPriority: 'MEDIUM',   requiresAck: false },
  ITP_RELEASE:          { label: 'Hold Point Released',     icon: '🟢', category: 'quality',     defaultPriority: 'HIGH',     requiresAck: true  },
  TIMESHEET_APPROVED:   { label: 'Timesheet Approved',      icon: '💰', category: 'payroll',     defaultPriority: 'LOW',      requiresAck: false },
  ENVIRONMENTAL_ALERT:  { label: 'Environmental Alert',     icon: '🌿', category: 'safety',      defaultPriority: 'HIGH',     requiresAck: true  },
  PROGRESS_MILESTONE:   { label: 'Project Milestone',       icon: '🏆', category: 'operational', defaultPriority: 'LOW',      requiresAck: false },
  CUSTOM_WHSO_ALERT:    { label: 'WHSO Alert',              icon: '🔔', category: 'safety',      defaultPriority: 'HIGH',     requiresAck: true  },
  CREDENTIAL_EXPIRY:    { label: 'Credential Expiry',       icon: '⏰', category: 'workforce',   defaultPriority: 'MEDIUM',   requiresAck: false },
  PAYSLIP_AVAILABLE:    { label: 'Payslip Available',       icon: '📄', category: 'payroll',     defaultPriority: 'LOW',      requiresAck: false },
}

export type NotificationPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type NotificationStatus = 'SENT' | 'DELIVERED' | 'ACKNOWLEDGED' | 'EXPIRED'

export interface NotificationAcknowledgement {
  employeeId: string
  employeeName: string
  acknowledgedAt: string
}

export interface CrewNotification<T = Record<string, unknown>> {
  id: string
  type: NotificationType
  priority: NotificationPriority
  status: NotificationStatus
  // Recipients
  employeeIds: string[]           // which crew members should receive this
  jobIds: string[]                // which jobs this relates to
  // Content
  heading: string
  body: string
  requiredAction?: string
  attachmentUrl?: string
  // Source
  sourceModule: string            // e.g. 'Incidents.tsx', 'SWMS.tsx'
  sourceRecordId?: string         // ID of the originating record
  // Lifecycle
  createdAt: string
  createdById: string
  createdByName: string
  expiresAt?: string
  requiresAck: boolean
  acknowledgements: NotificationAcknowledgement[]
  // Typed payload — specific to each notification type
  payload: T
}

// ── Typed payloads per notification type ──────────────────────────
export interface AccommodationPayload {
  [key: string]: unknown
  propertyName: string
  address: string
  keyholderName: string
  keyholderPhone: string
  checkIn: string
  checkOut: string
  coOccupants: string[]
  amenities: string[]
  bookingRef: string
}

export interface IncidentAlertPayload {
  [key: string]: unknown
  incidentRef: string
  incidentType: string
  severity: 'CRITICAL' | 'HIGH'
  location: string
  initialActions: string
  investigationStatus: string
}

export interface SWMSUpdatePayload {
  [key: string]: unknown
  swmsRef: string
  swmsTitle: string
  oldRevision: string
  newRevision: string
  changesSummary: string
  requiresReSign: boolean
}

export interface PermitToWorkPayload {
  [key: string]: unknown
  permitRef: string
  permitType: string
  siteAddress: string
  validFrom: string
  validTo: string
  conditions: string[]
  emergencyContacts: Array<{ name: string; role: string; phone: string }>
}

export interface ToolboxSignoffPayload {
  [key: string]: unknown
  toolboxRef: string
  topic: string
  presenter: string
  talkDate: string
  pdfUrl?: string
  isRemote: boolean
}

export interface PlantDefectPayload {
  [key: string]: unknown
  assetId: string
  assetName: string
  assetNumber: string
  defectDescription: string
  currentJobId: string
  currentJobNumber: string
  ceaseUseInstruction: string
}

export interface MaterialReadyPayload {
  [key: string]: unknown
  bomLineId: string
  materialDescription: string
  quantity: number
  unit: string
  stockStatus: 'ON_SITE' | 'WAREHOUSE'
  linkedTaskId?: string
  linkedTaskTitle?: string
}

export interface ITPReleasePayload {
  [key: string]: unknown
  holdPointRef: string
  holdPointDescription: string
  activityReleased: string
  releasedBy: string
  releasedAt: string
}

export interface TimesheetApprovedPayload {
  [key: string]: unknown
  payPeriodLabel: string
  payPeriodStart: string
  payPeriodEnd: string
  ordinaryHours: number
  overtimeHours: number
  nightLoadingHours: number
  grossEstimate: number
  adjustmentsMade: boolean
  adjustmentReason?: string
}

// ── 1A-02  Updated CrewMember interface ───────────────────────────
export interface EmergencyContact {
  name: string
  relationship: string
  phone: string
  altPhone?: string
}

export type RecallStatusType = 'NONE' | 'RECALL_SENT' | 'RECALLED' | 'RETURNED'

export interface CrewMember {
  // Core — maps to RosterNotification
  id: string
  employeeId: string
  employeeName: string
  jobId: string
  jobNumber: string
  jobName: string
  siteAddress: string
  date: string
  role: string
  shiftType: 'DAY' | 'NIGHT' | 'HALF' | 'HALF'
  supervisorName: string
  notes: string | null
  pushedAt: string
  pushedByName: string
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'ON_SITE' | 'COMPLETED' | 'RECALLED' | 'RECALL_SENT'
  confirmedAt: string | null
  rejectedAt: string | null
  rejectionCode: string | null
  rejectionNote: string | null
  startTime: string | null
  finishTime: string | null
  hoursWorked: number | null
  // Phase 1 additions
  emergencyContact: EmergencyContact | null
  unreadNotificationCount: number
  lastSyncTimestamp: string | null
  recallStatus: RecallStatusType
}

// ── 1A-03  RecallEvent interface ───────────────────────────────────
export type RecallReasonLabel =
  | 'Site shutdown — unplanned'
  | 'WHS incident — active'
  | 'WHS incident — investigation'
  | 'Delayed materials — key item not on site'
  | 'Severe weather — BOM warning issued'
  | 'Client instruction'
  | 'Regulator / Inspector on site'
  | 'Equipment failure'
  | 'Environmental incident'
  | 'Other'
  | 'Site access — revoked'
  | 'Weather — severe / lightning'
  | 'Site emergency'
  | 'End of shift'
  | 'Site access — revoked'
  | 'Weather — severe / lightning'
  | 'Site emergency'
  | 'End of shift'

export interface RecallCrewStatus {
  employeeId: string
  employeeName: string
  role: string
  statusAtRecall: string         // what their attendance status was when recall was issued
  recallSentAt: string
  acknowledgedAt: string | null
  returnedToSiteAt: string | null
}

export interface RecallEvent {
  id: string                     // auto-generated: RC-[jobNumber]-[seq] e.g. RC-TP001-001
  sequence: number               // sequential per project
  // Scope
  jobIds: string[]
  jobNumbers: string[]
  // Reason
  reason: RecallReasonLabel
  freeText: string | null
  musterPoint: string | null
  returnETA: string | null       // ISO time string
  urgency: 'IMMEDIATE' | 'END_OF_TASK' | 'END_OF_SHIFT'
  // Crew
  affectedCrew: RecallCrewStatus[]
  // Issuer
  issuedById: string
  issuedByName: string
  issuedByTier: string
  issuedAtAEST: string           // ISO datetime
  issuedAtUTC: string            // ISO datetime
  // Resolution
  allReturnedAt: string | null
  totalShutdownMinutes: number | null
  // Audit
  exportedAt: string | null      // when recall report PDF was generated
  exportedById: string | null
  exportedByName: string | null
  // Immutable flag — once created this record is permanent
  readonly immutable: true
}

// ── 1A-04  SiteStatusCard interface ───────────────────────────────
export type ShiftStatus = 'DAY_ACTIVE' | 'NIGHT_ACTIVE' | 'NOT_STARTED' | 'FINISHED' | 'RECALLED'

export interface WeatherSummary {
  condition: string
  tempC: number
  icon: string
  bomRef: string
  hasWarning: boolean
  warningType?: string
}

export interface SiteStatusCard {
  jobId: string
  jobNumber: string
  jobName: string
  siteAddress: string
  sitePostcode: string
  // Crew
  crewOnSite: number
  crewExpected: number
  crewPending: number
  // Shift
  currentShift: ShiftStatus
  shiftStartTime: string | null
  // Sync
  lastSyncAt: string | null
  devicesOnSite: number
  // WHS
  activeWHSIncidents: number      // red badge if > 0
  openPermits: number
  activeWHSOAlerts: number
  // Recall
  recallActive: boolean
  activeRecallId: string | null
  // Plant
  defectiveAssetsOnSite: number
  // Weather
  weather: WeatherSummary | null
}

// ── 1A-05  AssignableTask interface ───────────────────────────────
export type TaskSourceType = 'DRAWING' | 'WI_STEP' | 'AI_TASK' | 'PM_CREATED'
export type TaskPriority   = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW'
export type GoNoGoStatus   = 'GO' | 'CONDITIONAL_GO' | 'NO_GO' | 'NO_MATERIALS'

// ── 1A-06  MaterialStatus ──────────────────────────────────────────
export type MaterialStatus =
  | 'AVAILABLE_ON_SITE'
  | 'AVAILABLE_WAREHOUSE'
  | 'ON_ORDER_ETA'
  | 'ON_ORDER_NO_ETA'
  | 'NOT_ORDERED'
  | 'NOT_REQUIRED'

export const MATERIAL_STATUS_META: Record<MaterialStatus, {
  label: string
  badge: string
  text: string
  proceedable: boolean
}> = {
  AVAILABLE_ON_SITE:   { label: 'Available — On Site',     badge: 'bg-green-100',  text: 'text-green-700',  proceedable: true  },
  AVAILABLE_WAREHOUSE: { label: 'Available — Warehouse',   badge: 'bg-teal-100',   text: 'text-teal-700',   proceedable: true  },
  ON_ORDER_ETA:        { label: 'On Order — ETA known',    badge: 'bg-amber-100',  text: 'text-amber-700',  proceedable: false },
  ON_ORDER_NO_ETA:     { label: 'On Order — No ETA',       badge: 'bg-amber-100',  text: 'text-amber-800',  proceedable: false },
  NOT_ORDERED:         { label: 'Not Ordered',             badge: 'bg-red-100',    text: 'text-red-700',    proceedable: false },
  NOT_REQUIRED:        { label: 'Not Required',            badge: 'bg-gray-100',   text: 'text-gray-500',   proceedable: true  },
}

export interface TaskMaterial {
  bomLineId: string
  description: string
  quantity: number
  unit: string
  status: MaterialStatus
  poNumber?: string
  expectedDelivery?: string
  notes?: string
}

export interface AssignableTask {
  taskId: string
  taskSource: TaskSourceType
  // Source references
  drawingRef?: string            // "J1-S01 Rev 3" if from drawing
  drawingNumber?: string
  wiStepId?: string              // if from Work Instruction
  wiStepTitle?: string
  aiTaskId?: string              // if from AI task list
  aiSourceDoc?: string
  // Content
  title: string
  description: string
  specClauseRef?: string
  discipline: string
  priority: TaskPriority
  // Assignment
  assignedToIds: string[]
  assignedToNames: string[]
  jobId: string
  jobNumber: string
  targetDate: string
  shiftType: 'DAY' | 'NIGHT'
  // Materials
  materialsRequired: TaskMaterial[]
  goNoGoStatus: GoNoGoStatus
  noGoOverrideReason?: string
  // Completion
  photoRequired: boolean
  photoCount: number
  completionCriteria?: string
  holdPointLinkage?: string
  // Status
  pushedToMobile: boolean
  pushedAt?: string
  approvalStatus: 'DRAFT' | 'APPROVED' | 'ACTIVE'  // AI tasks start as DRAFT
}

// ── 1A-07  WHSSubmission interface ────────────────────────────────
export type WHSSubmissionType =
  | 'INCIDENT'
  | 'NEAR_MISS'
  | 'HAZARD_IDENTIFICATION'
  | 'UNSAFE_CONDITION'
  | 'SWMS_NON_COMPLIANCE'

export type WHSSubmissionStatus =
  | 'RECEIVED'
  | 'UNDER_REVIEW'
  | 'PENDING_CLOSE_OUT'
  | 'CLOSED'

export const WHS_SUBMISSION_TYPE_META: Record<WHSSubmissionType, {
  label: string
  platformModule: string
  platformRecordType: string
}> = {
  INCIDENT:             { label: 'Incident Report',         platformModule: 'WHS → Incidents',   platformRecordType: 'Incident' },
  NEAR_MISS:            { label: 'Near-miss Report',        platformModule: 'WHS → Incidents',   platformRecordType: 'Incident (near-miss)' },
  HAZARD_IDENTIFICATION:{ label: 'Hazard Identification',   platformModule: 'WHS → Inspections', platformRecordType: 'Corrective Action' },
  UNSAFE_CONDITION:     { label: 'Unsafe Condition',        platformModule: 'WHS → Inspections', platformRecordType: 'Corrective Action' },
  SWMS_NON_COMPLIANCE:  { label: 'SWMS Non-compliance',     platformModule: 'WHS → SWMS',        platformRecordType: 'SWMS Flag' },
}

export interface WHSSubmission {
  id: string
  reference: string              // e.g. WHS-TP001-0001
  type: WHSSubmissionType
  // Source
  submittedById: string
  submittedByName: string
  submittedByRole: string
  submittedAt: string
  // Location
  jobId: string
  jobNumber: string
  location: string
  gpsLat: number | null
  gpsLng: number | null
  // Content
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
  description: string
  photoCount: number
  // Platform link
  status: WHSSubmissionStatus
  platformWHSRecordId: string | null    // set when corresponding WHS module record is created
  platformWHSModule: string | null      // which WHS module the record was created in
  // Review
  reviewedById: string | null
  reviewedByName: string | null
  reviewedAt: string | null
  pmNotes: string | null
  actionsTaken: string[]
  // Close-out
  closedById: string | null
  closedByName: string | null
  closedAt: string | null
  // Regulatory
  regulatorNotified: boolean
  regulatorRef: string | null
  // Offline
  submittedOffline: boolean
  offlineDelayMinutes: number | null
}

// ── 1A-08  WHSOAlert interface ────────────────────────────────────
export type WHSOAlertType =
  | 'WHS_GENERAL'
  | 'WHS_HIGH_RISK'
  | 'ENVIRONMENTAL'
  | 'TRAFFIC_MANAGEMENT_CHANGE'
  | 'SCOPE_CHANGE'
  | 'SITE_ACCESS_CHANGE'
  | 'CLIENT_INSTRUCTION'
  | 'EMERGENCY'
  | 'PLANT_DEFECT'
  | 'WEATHER'
  | 'SWMS_UPDATE'
  | 'OTHER'

export type WHSOAlertSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type WHSOAlertStatus   = 'DRAFT' | 'SENT' | 'EXPIRED' | 'ARCHIVED'
export type WHSOAlertSource   = 'MANUAL' | 'AUTO_INCIDENT' | 'AUTO_SWMS' | 'AUTO_PLANT' | 'AUTO_WEATHER'

export const WHSO_ALERT_TYPE_META: Record<WHSOAlertType, { label: string }> = {
  WHS_GENERAL:              { label: 'WHS — General' },
  WHS_HIGH_RISK:            { label: 'WHS — High risk activity' },
  ENVIRONMENTAL:            { label: 'Environmental' },
  TRAFFIC_MANAGEMENT_CHANGE:{ label: 'Traffic management change' },
  SCOPE_CHANGE:             { label: 'Scope of works change' },
  SITE_ACCESS_CHANGE:       { label: 'Site access change' },
  CLIENT_INSTRUCTION:       { label: 'Client instruction' },
  EMERGENCY:                { label: 'Emergency' },
  PLANT_DEFECT:             { label: 'Plant / Equipment defect' },
  WEATHER:                  { label: 'Weather alert' },
  SWMS_UPDATE:              { label: 'SWMS update — re-sign required' },
  OTHER:                    { label: 'Other' },
}

export interface WHSOAlertAcknowledgement {
  employeeId: string
  employeeName: string
  acknowledgedAt: string
}

export interface WHSOAlert {
  id: string
  alertType: WHSOAlertType
  severity: WHSOAlertSeverity
  source: WHSOAlertSource
  status: WHSOAlertStatus
  // Platform WHS link (optional)
  linkedWHSRecordId: string | null
  linkedWHSModule: string | null     // e.g. 'WHS → Incidents', 'WHS → SWMS'
  // Recipients
  affectedJobIds: string[]
  affectedJobNumbers: string[]
  affectedCrewIds: string[]
  // Content
  heading: string
  body: string
  requiredAction: string | null
  attachmentUrl: string | null
  // Delivery
  acknowledgementRequired: boolean
  acknowledgements: WHSOAlertAcknowledgement[]
  acknowledgedCount: number
  totalRecipients: number
  // Lifecycle
  createdAt: string
  createdById: string
  createdByName: string
  sentAt: string | null
  expiresAt: string | null
  archivedAt: string | null
}
